import { useCallback } from 'react';
import { logError, logInfo } from '@utils/logger';

// Optimized options for iOS devices
const IOS_OPTIONS = {
  enableHighAccuracy: true,
  timeout: 30000,
  maximumAge: 0
};

// Fallback options with more lenient settings
const FALLBACK_OPTIONS = {
  enableHighAccuracy: false,
  timeout: 60000,
  maximumAge: 30000
};

export const useGeolocation = () => {
  const handleError = (error, onUpdate, options, retryCount = 0) => {
    let errorMessage = 'Unknown location error';
    const MAX_RETRIES = 5;

    switch (error.code) {
      case error.PERMISSION_DENIED:
        errorMessage = 'Location access denied. Please enable location services in your settings.';
        onUpdate?.({ error: errorMessage, status: 'disabled' });
        return;

      case error.POSITION_UNAVAILABLE:
        errorMessage = 'Unable to determine location. Please check your GPS signal.';
        break;

      case error.TIMEOUT:
        errorMessage = 'Location request timed out. Retrying with lower accuracy...';
        break;
    }

    if (retryCount < MAX_RETRIES) {
      if (retryCount === 0) {
        logInfo('Retrying location request with lower accuracy');
      }
      setTimeout(() => startTracking(onUpdate, FALLBACK_OPTIONS, retryCount + 1), 5000);
      return;
    }

    onUpdate?.({ error: errorMessage, status: 'unavailable' });
    logError('Location error:', { code: error.code, message: errorMessage });
  };

  const startTracking = useCallback((onUpdate, options = IOS_OPTIONS, retryCount = 0) => {
    if (!navigator.geolocation) {
      logError('Geolocation not supported');
      onUpdate?.({ error: 'Location services not supported on this device', status: 'unavailable' });
      return null;
    }

    const watchIdRef = { current: null };
    let isActive = true;

    if (retryCount === 0) {
      logInfo('Location tracking started');
    }

    // Get initial position with shorter timeout
    navigator.geolocation.getCurrentPosition(
      (position) => {
        if (!isActive) return;

        console.log('[Geolocation] Initial position:', position.coords);
        const point = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          altitude: position.coords.altitude,
          speed: position.coords.speed,
          heading: position.coords.heading,
          timestamp: new Date().toISOString(),
          status: 'available'
        };
        
        onUpdate?.(point);

        watchIdRef.current = navigator.geolocation.watchPosition(
          (position) => {
            if (!isActive) return;
            const point = {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              accuracy: position.coords.accuracy,
              altitude: position.coords.altitude,
              speed: position.coords.speed,
              heading: position.coords.heading,
              timestamp: new Date().toISOString(),
              status: 'available'
            };
            onUpdate?.(point);
          },
          (error) => handleError(error, onUpdate, options, retryCount),
          options
        );
      },
      (error) => handleError(error, onUpdate, options, retryCount),
      { ...options, timeout: 10000 }
    );

    // Return cleanup function
    return () => {
      isActive = false;
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
    };
  }, []);

  const stopTracking = useCallback(() => {
    logInfo('Location tracking stopped');
  }, []);

  return { startTracking, stopTracking };
};